import _ from 'lodash';
import { useMutation, useQuery } from '@apollo/client';

// Data
import { getCurrUser } from './user.service';
import { APPLY_ATTENDANCE_DEFAULT_TO_ASSESSMENTS, CREATE_COMPANY, GET_COMPANY_BY_DOMAIN, GET_COMPANY_BY_ID, REGISTER_COMPANY, UPDATE_COMPANY } from '../queries/company.queries';
import { IRegisterCompanyProps } from '../../components/Company/RegisterCompanyForm/register-company-form.interfaces';
import { IUpdateCompanyProps } from '../../components/Company/UpdateCompanyForm/update-company-form.interfaces';
import { RestService } from '../utils/api.service';

export const getActiveCompanyId = () => {
  const { user } = getCurrUser();

  return _.get(user, 'settings.activeCompanyId');
};

export const getCompany = (_id: string) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, ...rest } = useQuery(GET_COMPANY_BY_ID, {
    skip: !_id,
    variables: { _id },
    fetchPolicy: 'cache-and-network'
  });

  return { ...rest, company: _.get(data, 'company') as ICompany };
};

export const getCompanyByDomain = (domain: string) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, ...rest } = useQuery(GET_COMPANY_BY_DOMAIN, {
    skip: !domain,
    variables: { domain },
    fetchPolicy: 'network-only'
  });

  return { ...rest, company: _.get(data, 'companyByDomain') };
};

export const buildRegisterCompany = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [register, ...rest] = useMutation(REGISTER_COMPANY);

  return {
    ...rest,
    registerCompany: (data: IRegisterCompanyProps) =>
      register({ variables: { data } })
  }
};

export interface ICreateCompanyInput {
  name: string;
  timezone: string;
}

// Global-admin-only association creation. Refetches the company switcher so the
// new association appears there immediately for the creating admin.
export const buildCreateCompany = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [create, ...rest] = useMutation(CREATE_COMPANY, {
    refetchQueries: ['GetCompanySelector'],
  });

  return {
    ...rest,
    createCompany: (companyData: ICreateCompanyInput) =>
      create({ variables: { companyData } }),
  };
};

export const buildUpdateCompany = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [update, ...rest] = useMutation(UPDATE_COMPANY);
  const activeCompanyId = getActiveCompanyId();

  return {
    ...rest,
    updateCompany: (data: IUpdateCompanyProps) =>
      update({ variables: { _id: activeCompanyId, data } })
  };
}

// Backfills the association's `isAttendanceRequestedDefault` onto assessments that never
// had it set; the mutation returns counts not the touched rows, so refetch the assessments.
export const buildApplyAttendanceDefaultToAssessments = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, ...rest] = useMutation(APPLY_ATTENDANCE_DEFAULT_TO_ASSESSMENTS, {
    refetchQueries: ['GetAssessments', 'GetAssessment'],
  });
  const activeCompanyId = getActiveCompanyId();

  return {
    ...rest,
    applyAttendanceDefaultToAssessments: () =>
      mutation({ variables: { _id: activeCompanyId } }),
  };
}

export const handleCompanyLogoUpload = (companyId: string, files: FileList, token: string | undefined | null) => {
  const data = new FormData();

  data.set('uploadToCloudStorage', 'true');
  data.append('assetFile', files[0], files[0].name);

  return RestService.upload(`/companies/${companyId}/logo`, data, token);
}
